import React, { useEffect, useState, useRef, useCallback } from "react";
import { getHomepageCache, fetchHomepage } from "../lib/homepage";
import { Link } from "react-router-dom";
import api, { imgW } from "../lib/api";
import { Seo } from "../components/Seo";
import { HeroStamp, HomeLeCard } from "../components/Limited";
import { trackEvent } from "../lib/seo";
import { Pic } from "../components/Pic";

const HERO_SLIDES = ["/home/p-ultra.jpg", "/home/p-nutella.jpg", "/home/p-pistachio.jpg"];

const CATS = [
  { to: "/cookies", img: "/home/p-pistachio.jpg", imgKey: "tile_cookies", labelKey: "cat_cookies_label", label: "Cookies", descKey: "cat_cookies_desc", desc: "New-Yorkais, cœur fondant · dès 3,00 €" },
  { to: "/brownies", img: "/home/col-brownies.jpg", imgKey: "tile_brownies", labelKey: "cat_brownies_label", label: "Brownies", descKey: "cat_brownies_desc", desc: "Fondants, intenses et généreux" },
  { to: "/cupcookies", img: "/home/col-cupcookies.jpg", imgKey: "tile_cupcookies", labelKey: "cat_cupcookies_label", label: "Cupcookies", descKey: "cat_cupcookies_desc", desc: "Le cookie en version garnie" },
  { to: "/composer-ma-box", img: "/home/p-macadamia.jpg", imgKey: "tile_box", labelKey: "cat_box_label", label: "Box", descKey: "cat_box_desc", desc: "Compose la tienne · dès 12,00 €" },
];

const BENTO = [
  { img: "/home/sav-pistache.jpg", imgKey: "sav_pistache", capKey: "bento_cap1", cap: "Pistache", tall: true },
  { img: "/home/sav-chocolat.jpg", imgKey: "sav_chocolat", capKey: "bento_cap2", cap: "Chocolat", tall: false },
  { img: "/home/sav-noisette.jpg", imgKey: "sav_noisette", capKey: "bento_cap3", cap: "Noisette", tall: true },
  { img: "/home/sav-speculoos.jpg", imgKey: "sav_speculoos", capKey: "bento_cap4", cap: "Spéculoos", tall: false },
];

export const VALUES = [
  { titleKey: "val1_title", title: "Chocolat de qualité", descKey: "val1_desc", desc: "En gros morceaux, pour un cœur qui coule vraiment.", icon: <path d="M4 4h16v16H4z M4 12h16 M12 4v16" /> },
  { titleKey: "val2_title", title: "Recettes faites maison", descKey: "val2_desc", desc: "Préparées à la main à Palaiseau, en petites fournées.", icon: <path d="M12 21c-4-3-8-6.5-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 10c0 4.5-4 8-8 11z" /> },
  { titleKey: "val3_title", title: "Gourmandise à partager", descKey: "val3_desc", desc: "Pour le goûter, les fêtes et tous les moments qui comptent.", icon: <path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6L3.3 9.3l6.1-.7z" /> },
];

const Words = ({ text, start = 0 }) => String(text).split(/(\s+)/).filter(Boolean).reduce((acc, w) => {
  if (/^\s+$/.test(w)) acc.out.push(w);
  else acc.out.push(<span className="rw" key={acc.k}><span style={{ "--i": acc.k++ }}>{w}</span></span>);
  return acc;
}, { out: [], k: start }).out;

export const MANI_DEFAULT = "Des cookies *épais*, des brownies *fondants*, faits maison *chaque jour* à Palaiseau. À l'unité ou en box, pour tous les moments qui comptent.";

function Manifesto({ kicker, text }) {
  let n = 0;
  const parts = String(text).split(/(\*[^*]+\*)/).filter(Boolean).map((seg, si) => {
    const em = /^\*.*\*$/.test(seg);
    const ws = (em ? seg.slice(1, -1) : seg).split(/(\s+)/).filter(Boolean).map((w) => (/^\s+$/.test(w) ? w : <span className="w" key={`w${n}`} data-w={n++}>{w}</span>));
    return em ? <em key={si}>{ws}</em> : <React.Fragment key={si}>{ws}</React.Fragment>;
  });
  return (
    <section className="cs-mani" id="manifeste" aria-label="Notre promesse" data-testid="home-manifesto">
      <div className="cs-mani-pin">
        <p className="cs-mani-k">{kicker}</p>
        <p className="cs-mani-t">{parts}</p>
        <img className="cs-mani-m" src="/static/home/masc-cookie-orange.png" alt="" width="360" height="291" loading="lazy" decoding="async" />
      </div>
    </section>
  );
}

const BAND = ["Cookies", "Brownies", "Cupcookies", "Box", "Cookies", "Brownies", "Cupcookies", "Box"];
const BigBand = () => (
  <div className="cs-bm" aria-hidden="true" data-testid="home-big-band"><div className="cs-bm-track">
    {BAND.map((w, i) => <React.Fragment key={i}><span className={i % 2 ? "o" : ""}>{w}</span><b>•</b></React.Fragment>)}
  </div></div>
);

function useScrollFx(rootRef, dep) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    root.classList.add("js-anim");
    root.querySelectorAll(".rv-group").forEach((g) => [...g.children].forEach((c, i) => c.style.setProperty("--i", i)));
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }), { threshold: 0.18, rootMargin: "0px 0px -8% 0px" });
    root.querySelectorAll("[data-rise]:not([data-now]):not(.in),.rv:not(.in),.rv-group:not(.in),.rv-clip:not(.in)").forEach((el) => io.observe(el));
    const now = setTimeout(() => root.querySelectorAll("[data-now]").forEach((h) => h.classList.add("in")), 150);
    const mani = root.querySelector(".cs-mani"), masc = root.querySelector(".cs-mani-m"), bm = root.querySelector(".cs-bm-track");
    const words = mani ? [...mani.querySelectorAll(".w")] : [];
    let ticking = false;
    const tick = () => {
      ticking = false;
      const vh = window.innerHeight;
      if (mani) {
        const r = mani.getBoundingClientRect();
        const prog = Math.min(1, Math.max(0, (-r.top + vh * 0.15) / (r.height - vh * 0.7)));
        const n = Math.round(prog * words.length);
        words.forEach((w, i) => w.classList.toggle("lit", i < n));
        if (masc) masc.style.setProperty("--my", `${-prog * 120}px`);
      }
      if (bm) { const rb = bm.getBoundingClientRect(); if (rb.bottom > 0 && rb.top < vh) bm.style.transform = `translateX(${-((vh - rb.top) * 0.6)}px)`; }
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(tick); } };
    window.addEventListener("scroll", onScroll, { passive: true });
    tick();
    return () => { io.disconnect(); clearTimeout(now); window.removeEventListener("scroll", onScroll); };
  }, [rootRef, dep]);
}

function Reviews({ items }) {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const step = () => {
    const el = ref.current;
    if (!el) return 1;
    const s = el.querySelectorAll(".cs-slide");
    return s.length > 1 ? s[1].offsetLeft - s[0].offsetLeft : el.clientWidth;
  };
  const go = useCallback((i) => {
    const el = ref.current; if (!el) return;
    const max = items.length - 1;
    const idx = i < 0 ? max : i > max ? 0 : i;
    el.scrollTo({ left: idx * step(), behavior: "smooth" });
  }, [items.length]);
  const onScroll = () => { const el = ref.current; if (el) setActive(Math.round(el.scrollLeft / step())); };
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let paused = false;
    const stop = () => { paused = true; };
    ["pointerdown", "touchstart"].forEach((e) => el.addEventListener(e, stop, { passive: true }));
    const t = setInterval(() => {
      if (document.hidden || paused) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + step(), behavior: "smooth" });
    }, 6000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <div className="cs-slider" ref={ref} onScroll={onScroll} tabIndex={0} aria-label="Avis clients" data-testid="reviews-slider">
        {items.map((t, i) => (
          <figure className="cs-slide" key={t.id || i}>
            <div className="cs-stars" aria-label="5 étoiles">★★★★★</div>
            <blockquote>« {t.text} »</blockquote>
            <figcaption>{t.name}{t.source && <small className="cs-rev-src">{t.source}</small>}</figcaption>
          </figure>
        ))}
      </div>
      <div className="cs-sctrl">
        <button className="cs-arrow" type="button" aria-label="Avis précédent" data-testid="reviews-prev" onClick={() => go(active - 1)}>←</button>
        <button className="cs-arrow" type="button" aria-label="Avis suivant" data-testid="reviews-next" onClick={() => go(active + 1)}>→</button>
        <div className="cs-sdots">
          {items.map((_, i) => (
            <button key={i} type="button" aria-label={`Avis ${i + 1}`} aria-current={i === active} onClick={() => go(i)} />
          ))}
        </div>
      </div>
    </>
  );
}

export default function Home() {
  const [content, setContent] = useState(getHomepageCache);
  const [testimonials, setTestimonials] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const rootRef = useRef(null);
  useScrollFx(rootRef, content);

  useEffect(() => {
    fetchHomepage().then(setContent).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {});
  }, []);

  const t = (k, d) => (content[k] && String(content[k]).trim()) ? content[k] : d;
  const show = (k) => content[k] !== false;
  const pw = (u, w) => imgW(u, w);
  const heroSlides = (content.hero_slides && content.hero_slides.length) ? content.hero_slides : (window.__CS_HERO__ || HERO_SLIDES);
  const defCats = CATS.map((c) => ({ ...c, img: content[c.imgKey] || c.img, label: t(c.labelKey, c.label), desc: t(c.descKey, c.desc) }));
  const cats = Array.isArray(content.home_cats) && content.home_cats.length
    ? content.home_cats.filter((c) => c.visible !== false && c.label).map((c) => ({ to: c.to || "/", img: c.img || "/home/p-pistachio.jpg", label: c.label, desc: c.desc || "" }))
    : defCats;
  const bento = BENTO.map((b) => ({ ...b, img: content[b.imgKey] || b.img, cap: t(b.capKey, b.cap) }));
  const values = VALUES.map((v) => ({ ...v, title: t(v.titleKey, v.title), desc: t(v.descKey, v.desc) }));
  const aboutImg = content.about_image || "/home/hero-fouet.jpg";

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const tmr = setInterval(() => setHeroIdx((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(tmr);
  }, [heroSlides.length]);

  const [restReady, setRestReady] = useState(false);
  useEffect(() => {
    const go = () => setTimeout(() => setRestReady(true), 800);
    if (document.readyState === "complete") { const t = go(); return () => clearTimeout(t); }
    window.addEventListener("load", go, { once: true });
    return () => window.removeEventListener("load", go);
  }, []);

  const reviews = testimonials;
  const heroAlts = content.hero_alts || [];

  return (
    <div className="cs-home" ref={rootRef}>
      <Seo />
      <style>{CS_CSS}</style>

      {/* HERO */}
      <section className="cs-hero" id="top">
        {heroSlides.map((src, i) => (
          <div key={i} className={`cs-hslide${i === (heroIdx % heroSlides.length) ? " on" : ""}`}>{(i === 0 || restReady) && <Pic src={src} width="1600" height="1000" alt={(heroAlts[i] || "").trim() || "Cookie new-yorkais Crunchy Sweet, cœur coulant"} loading={i === 0 ? "eager" : "lazy"} fetchPriority={i === 0 ? "high" : "low"} decoding={i === 0 ? "sync" : "async"} />}</div>
        ))}
        <div className="cs-hero-txt">
          <p className="cs-eyebrow">{t("hp_hero_eyebrow", "Cookies New-Yorkais · faits main à Palaiseau")}</p>
          <h1 lang="en" data-rise="" data-now="" data-testid="home-hero-title"><Words text={t("hp_hero_title1", "Crunchy outside,")} /><br /><span className="it"><Words text={t("hp_hero_accent", "sweet")} start={2} /></span> <Words text={t("hp_hero_title2", "inside.")} start={3} /></h1>
          <p className="cs-lede">{t("hp_hero_lede", "Épais, généreux, le cœur qui coule. Cuits à la main pour le jour où vous les dégustez.")}</p>
          <div className="cs-ctas">
            <a className="cs-btn cs-btn-orange" href="#categories" data-testid="home-hero-cta1" onClick={() => trackEvent("home_discover", "hero")}>{t("hp_hero_cta1", "Découvrir nos gourmandises")}</a>
            <Link className="cs-btn cs-btn-cream" to="/composer-ma-box" data-testid="home-hero-cta2">{t("hp_hero_cta2", "Composer ma box")}</Link>
          </div>
        </div>
        <a className="cs-scroll-cue" href="#categories" aria-label="Voir la suite"></a>
        <HeroStamp />
      </section>

      {show("show_manifesto") && <Manifesto kicker={t("hp_mani_kicker", "Crunchy Sweet")} text={t("hp_mani_text", MANI_DEFAULT)} />}
      {show("show_big_band") && <BigBand />}

      {/* NOS GOURMANDISES */}
      {show("show_categories") && (<section className="cs-sec" id="categories"><div className="wrap">
        <div className="cs-sec-head"><div>
          <h2 data-rise=""><Words text={t("hp_cat_title_pre", "Nos ")} /> <span className="it cs-accent"><Words text={t("hp_cat_accent", "gourmandises")} start={3} /></span></h2>
          <p className="rv">{t("hp_cat_sub", "Faites maison chaque jour, à l'unité ou en box.")}</p>
        </div></div>
        <div className="cs-cats rv-group">
          {cats.map((c, i) => (
            <Link className="cs-cat" to={c.to} key={`${c.to}-${i}`} data-testid={`home-cat-${i}`}>
              <img src={pw(c.img, 800)} srcSet={`${pw(c.img, 400)} 400w, ${pw(c.img, 800)} 800w`} sizes="(max-width:1000px) 46vw, 25vw" alt="" loading="lazy" decoding="async" width="800" height="1000" />
              <span className="cs-cat-label"><b>{c.label}</b><span>{c.desc}</span></span>
              <span className="cs-cat-go" aria-hidden="true">→</span>
            </Link>
          ))}
        </div>
      </div></section>)}
      <HomeLeCard />

      {/* VALEURS + BENTO */}
      {show("show_values") && (<section className="cs-sec cs-values" id="valeurs"><div className="wrap">
        <div className="cs-vtxt">
          <p className="cs-eyebrow">{t("hp_val_eyebrow", "Nos ingrédients")}</p>
          <h2 data-rise=""><Words text={t("hp_val_title_pre", "Des saveurs uniques, ")} /> <span className="it"><Words text={t("hp_val_title_accent", "des ingrédients qui font la différence.")} start={4} /></span></h2>
          <p className="cs-vlede">{t("hp_val_lede", "Pistache, noisette, chocolat, spéculoos : on choisit ce qu'il y a de meilleur et on n'économise jamais sur la garniture.")}</p>
          <ul className="cs-vlist rv-group">
            {values.map((v) => (
              <li key={v.titleKey}>
                <span className="cs-vic" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{v.icon}</svg></span>
                <div><b>{v.title}</b><span>{v.desc}</span></div>
              </li>
            ))}
          </ul>
        </div>
        <div className="cs-bento rv-group">
          {bento.map((b) => (
            <figure className={`cs-tile${b.tall ? " tall" : ""}`} key={b.capKey}><img src={pw(b.img, 700)} srcSet={`${pw(b.img, 400)} 400w, ${pw(b.img, 700)} 700w`} sizes="(max-width:1000px) 46vw, 22vw" width="700" height="700" alt={b.cap} loading="lazy" decoding="async" /><figcaption>{b.cap}</figcaption></figure>
          ))}
        </div>
      </div></section>)}

      {/* AVIS */}
      {show("show_reviews") && reviews.length > 0 && (<section className="cs-sec"><div className="wrap">
        <div className="cs-sec-head"><h2 data-rise=""><Words text={t("hp_rev_title_pre", "Vous en parlez ")} /> <span className="it cs-accent"><Words text={t("hp_rev_accent", "mieux que nous")} start={4} /></span></h2></div>
        <Reviews items={reviews} />
      </div></section>)}

      {/* À PROPOS */}
      {show("show_about") && (<section className="cs-sec cs-about" id="apropos"><div className="wrap">
        <div className="cs-about-pic rv-clip" data-testid="home-about-pic"><img src={pw(aboutImg, 900)} srcSet={`${pw(aboutImg, 520)} 520w, ${pw(aboutImg, 900)} 900w`} sizes="(max-width:900px) 92vw, 46vw" width="900" height="1125" alt="Fouet nappé de chocolat fondu" loading="lazy" decoding="async" /></div>
        <div className="cs-about-txt">
          <p className="cs-eyebrow">{t("hp_about_eyebrow", "À propos")}</p>
          <h2 data-rise=""><Words text={t("hp_about_title_pre", "Fait maison, ")} /> <span className="it cs-accent"><Words text={t("hp_about_accent", "avec amour.")} start={2} /></span></h2>
          <p>{t("hp_about_p1", "Crunchy Sweet, c'est une pâtisserie artisanale installée à Palaiseau. Des cookies New-Yorkais épais, des brownies ultra fondants et des créations maison, préparés à la main avec des ingrédients de qualité.")}</p>
          <p>{t("hp_about_p2", "Pour un goûter, un anniversaire, une baby shower, un mariage ou l'Aïd, chaque commande est préparée avec soin, jusqu'à la boîte.")}</p>
          <div className="cs-ctas">
            <Link className="cs-btn cs-btn-orange" to="/cookies" data-testid="home-about-cta1">{t("hp_about_cta1", "Lire notre histoire")}</Link>
            <Link className="cs-btn cs-btn-line" to="/evenements" data-testid="home-about-cta2">{t("hp_about_cta2", "Un événement ? Demander un devis")}</Link>
          </div>
        </div>
      </div></section>)}
    </div>
  );
}

const CS_CSS = `
.cs-home{--orange:#C6531A;--orange-deep:#A8410F;--orange-ink:#B44A16;--apricot:#F6D9C3;--peach:#FBEDE2;--paper:#FFF8F1;--bisque:#EDD4BB;--cocoa:#2B1A12;--cocoa-2:#4A3628;--muted:#6E5546;--line:#E7D3C2;--white:#fff;--display:'Bricolage Grotesque',ui-sans-serif,system-ui,sans-serif;--serif:'Instrument Serif',Georgia,serif;background:var(--paper);color:var(--cocoa);font-family:var(--display);font-size:17px;line-height:1.5;overflow-x:clip}
.cs-home img{display:block;width:100%;height:100%;object-fit:cover}
.cs-home .wrap{max-width:1320px;margin:0 auto;padding:0 20px}
.cs-home .it{font-family:var(--serif);font-style:italic;font-weight:400;letter-spacing:-0.01em}
.cs-home h1,.cs-home h2,.cs-home h3{margin:0;font-weight:800;letter-spacing:-0.04em;text-wrap:balance}
.cs-home h2{font-size:clamp(34px,5vw,64px);line-height:1.02}
.cs-home section{scroll-margin-top:90px}
.cs-accent{color:var(--orange-ink)}
.cs-eyebrow{margin:0;font-size:13px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--orange-ink)}
.cs-btn{min-height:52px;padding:0 26px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;gap:8px;text-decoration:none;font-weight:700;font-size:17px;border:0;white-space:nowrap;transition:background .2s,color .2s}
.cs-btn-orange{background:var(--orange-ink);color:var(--white)}.cs-btn-orange:hover{background:var(--orange-deep)}
.cs-btn-cream{background:var(--paper);color:var(--cocoa)}.cs-btn-cream:hover{background:var(--white)}
.cs-btn-line{background:transparent;color:var(--cocoa);border:1.5px solid var(--cocoa)}.cs-btn-line:hover{background:var(--cocoa);color:var(--paper)}

.cs-hero{position:relative;min-height:clamp(540px,86vh,840px);display:grid;place-items:center;text-align:center;color:var(--paper);overflow:hidden;background:var(--cocoa);isolation:isolate}
.cs-hslide{position:absolute;inset:0;opacity:0;transition:opacity 1.4s ease;z-index:-2}
.cs-hslide.on{opacity:1}
.cs-hslide img{object-fit:cover;object-position:center;transform:scale(1.35);transition:transform 9s ease-out}
.cs-hslide.on img{transform:scale(1.18)}
.cs-hero::after{content:"";position:absolute;inset:0;z-index:-1;background:rgba(30,16,9,.5)}
.cs-hero-txt{width:100%;max-width:1100px;margin:0 auto;padding:96px 20px;display:flex;flex-direction:column;align-items:center;gap:24px}
.cs-hero .cs-eyebrow{color:var(--paper);opacity:.92}
.cs-hero h1{font-size:clamp(54px,10vw,158px);line-height:.88;letter-spacing:-0.055em;text-shadow:0 4px 40px rgba(0,0,0,.25)}
.cs-hero h1 .it{color:#F4A06C;letter-spacing:-0.02em}
.cs-lede{color:var(--paper);font-size:clamp(18px,1.6vw,22px);max-width:38ch;margin:0}
.cs-ctas{display:flex;gap:12px;flex-wrap:wrap;justify-content:center}
.cs-scroll-cue{position:absolute;bottom:26px;left:50%;transform:translateX(-50%);width:28px;height:44px;border:2px solid rgba(255,248,241,.7);border-radius:999px}
.cs-scroll-cue::after{content:"";position:absolute;left:50%;top:8px;width:4px;height:8px;margin-left:-2px;border-radius:2px;background:var(--paper);animation:cscue 1.8s ease-in-out infinite}
@keyframes cscue{0%{opacity:0;transform:translateY(0)}40%{opacity:1}100%{opacity:0;transform:translateY(14px)}}

.cs-sec{padding-block:84px}
.cs-sec-head{display:flex;justify-content:space-between;align-items:flex-end;gap:20px;flex-wrap:wrap;margin-bottom:32px}
.cs-sec-head p{margin:10px 0 0;color:var(--muted);font-size:18px;max-width:46ch}

.cs-cats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px}
.cs-cat{position:relative;display:block;aspect-ratio:3/4;border-radius:26px;overflow:hidden;text-decoration:none;color:var(--paper);background:var(--bisque)}
.cs-cat img{transition:transform .7s ease}
.cs-cat:hover img{transform:scale(1.06)}
.cs-cat::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,transparent 45%,rgba(30,16,9,.72))}
.cs-cat-label{position:absolute;left:20px;right:70px;bottom:20px;z-index:1;display:flex;flex-direction:column;gap:2px}
.cs-cat-label b{font-size:30px;letter-spacing:-0.03em;line-height:1}
.cs-cat-label span{font-size:14.5px;opacity:.92}
.cs-cat-go{position:absolute;right:18px;bottom:20px;z-index:1;width:44px;height:44px;border-radius:999px;background:var(--paper);color:var(--cocoa);display:grid;place-items:center;font-size:18px;transition:background .2s,color .2s}
.cs-cat:hover .cs-cat-go{background:var(--orange);color:var(--white)}

.cs-values{background:var(--cocoa);color:var(--paper)}
.cs-values .wrap{display:grid;grid-template-columns:minmax(0,.9fr) minmax(0,1.1fr);gap:56px;align-items:center}
.cs-vtxt{display:flex;flex-direction:column;gap:24px;min-width:0}
.cs-vtxt .cs-eyebrow{color:#F4A06C}
.cs-values h2{color:var(--paper)}
.cs-values h2 .it{color:#F4A06C}
.cs-vlede{margin:0;font-size:18px;color:#D9C6B6;max-width:44ch}
.cs-vlist{list-style:none;margin:0;padding:0;display:flex;flex-direction:column}
.cs-vlist li{display:flex;align-items:center;gap:16px;padding:16px 0;border-top:1px solid rgba(255,248,241,.14)}
.cs-vlist li:last-child{border-bottom:1px solid rgba(255,248,241,.14)}
.cs-vic{width:44px;height:44px;flex:none;border-radius:999px;background:var(--orange);display:grid;place-items:center;color:var(--paper)}
.cs-vlist b{display:block;font-size:19px}
.cs-vlist span{font-size:15px;color:#BFA894}
.cs-bento{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));grid-template-rows:repeat(3,minmax(0,170px));gap:12px;min-width:0}
.cs-tile{position:relative;border-radius:22px;overflow:hidden;background:var(--cocoa-2);margin:0}
.cs-tile img{transition:transform .7s ease}
.cs-tile:hover img{transform:scale(1.07)}
.cs-tile.tall{grid-row:span 2}
.cs-tile figcaption{position:absolute;left:12px;bottom:12px;background:rgba(255,248,241,.94);color:var(--cocoa);border-radius:999px;padding:6px 14px;font-family:var(--serif);font-style:italic;font-size:20px}

.cs-slider{display:flex;gap:20px;overflow-x:auto;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none;padding-bottom:4px}
.cs-slider::-webkit-scrollbar{display:none}
.cs-slide{flex:0 0 calc((100% - 40px)/3);scroll-snap-align:start;margin:0;background:var(--peach);border-radius:26px;padding:30px;display:flex;flex-direction:column;justify-content:space-between;gap:22px;min-width:0}
.cs-stars{color:var(--orange-ink);font-size:18px;letter-spacing:3px}
.cs-slide blockquote{margin:0;font-family:var(--serif);font-size:26px;line-height:1.25}
.cs-slide figcaption{font-size:15px;font-weight:700}
.cs-rev-src{display:block;font-size:12px;font-weight:500;opacity:.7;margin-top:2px}
.cs-sctrl{display:flex;align-items:center;gap:14px;margin-top:22px}
.cs-arrow{width:48px;height:48px;border-radius:999px;border:1.5px solid var(--cocoa);background:transparent;color:var(--cocoa);font-size:20px;display:grid;place-items:center;cursor:pointer;transition:background .2s,color .2s}
.cs-arrow:hover{background:var(--cocoa);color:var(--paper)}
.cs-sdots{display:flex;margin-left:auto}
.cs-sdots button{position:relative;width:44px;height:44px;padding:0;border:0;background:transparent;cursor:pointer}
.cs-sdots button::before{content:"";position:absolute;top:50%;left:50%;width:10px;height:10px;border-radius:999px;background:var(--line);transform:translate(-50%,-50%);transition:width .2s,background-color .2s}
.cs-sdots button[aria-current="true"]::before{background:var(--orange-ink);width:28px}

.cs-about .wrap{display:flex;flex-wrap:wrap;gap:56px;align-items:center}
.cs-about-pic{flex:1 1 420px;min-width:0;aspect-ratio:4/5;border-radius:28px;overflow:hidden;max-width:100%}
.cs-about-txt{flex:1 1 420px;min-width:0;display:flex;flex-direction:column;gap:20px}
.cs-about-txt p{margin:0;font-size:18px;color:var(--cocoa-2);max-width:52ch}

.cs-mani{position:relative;height:220vh;background:var(--paper)}
.cs-mani-pin{position:sticky;top:0;height:100vh;display:flex;flex-direction:column;justify-content:center;gap:22px;max-width:1240px;margin:0 auto;padding:90px 20px 20px}
.cs-mani-k{margin:0;font-size:14px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--orange-ink)}
.cs-mani-t{margin:0;font-size:clamp(34px,6.2vw,92px);line-height:1.02;font-weight:800;letter-spacing:-0.045em;max-width:16ch;text-wrap:balance}
.cs-mani-t em{font-family:var(--serif);font-weight:400;letter-spacing:-0.01em;color:var(--orange-ink)}
.js-anim .cs-mani-t .w{opacity:.52;transition:opacity .25s linear}
.js-anim .cs-mani-t em .w{opacity:.76}
.cs-mani-t .w.lit{opacity:1}
.cs-home img.cs-mani-m{position:absolute;right:max(20px,calc((100vw - 1240px)/2 + 20px));bottom:12vh;width:clamp(90px,12vw,170px);height:auto;object-fit:contain;transform:rotate(-8deg) translateY(var(--my,0));transition:transform .2s linear}
.cs-bm{overflow:hidden;background:var(--orange);color:var(--paper);padding-block:18px;white-space:nowrap}
.cs-bm-track{display:flex;align-items:center;gap:34px;width:max-content;font-size:clamp(48px,9vw,130px);font-weight:800;letter-spacing:-0.05em;line-height:1;will-change:transform}
.cs-bm-track .o{color:transparent;-webkit-text-stroke:2px var(--paper)}
.cs-bm-track b{font-size:.4em;color:var(--cocoa)}
.js-anim [data-rise] .rw{display:inline-block;overflow:hidden;vertical-align:bottom;padding-bottom:.08em;margin-bottom:-.08em}
.js-anim [data-rise] .rw>span{display:inline-block;transform:translateY(105%);transition:transform .9s cubic-bezier(.2,.75,.15,1);transition-delay:calc(var(--i)*70ms)}
.js-anim [data-rise].in .rw>span{transform:none}
.js-anim .rv{opacity:0;transform:translateY(30px);transition:opacity .8s ease,transform .8s cubic-bezier(.2,.75,.15,1)}
.js-anim .rv.in{opacity:1;transform:none}
.js-anim .rv-group>*{opacity:0;transform:translateY(46px) scale(.97);transition:opacity .8s ease,transform .9s cubic-bezier(.2,.75,.15,1);transition-delay:calc(var(--i,0)*110ms)}
.js-anim .rv-group.in>*{opacity:1;transform:none}
.js-anim .rv-clip{clip-path:inset(18% 10% 18% 10% round 28px);transition:clip-path 1.2s cubic-bezier(.2,.75,.15,1)}
.js-anim .rv-clip img{transform:scale(1.25);transition:transform 1.4s cubic-bezier(.2,.75,.15,1)}
.js-anim .rv-clip.in{clip-path:inset(0 0 0 0 round 28px)}
.js-anim .rv-clip.in img{transform:none}

@media (max-width:1000px){
  .cs-cats{grid-template-columns:repeat(2,minmax(0,1fr))}
  .cs-values .wrap{grid-template-columns:minmax(0,1fr);gap:40px}
  .cs-slide{flex-basis:calc((100% - 20px)/2)}
}
@media (max-width:640px){
  .cs-mani{height:200vh}.cs-mani-t{font-size:clamp(32px,10vw,46px)}.cs-home img.cs-mani-m{bottom:8vh}
  .cs-sec{padding-block:58px}
  .cs-cats{gap:10px}
  .cs-cat{border-radius:20px}
  .cs-cat-label{left:12px;right:12px;bottom:54px}
  .cs-cat-label b{font-size:21px}
  .cs-cat-label span{display:none}
  .cs-cat-go{width:36px;height:36px;right:12px;bottom:12px}
  .cs-bento{grid-template-rows:repeat(3,minmax(0,120px));gap:8px}
  .cs-tile figcaption{font-size:17px;padding:5px 11px}
  .cs-about-pic{aspect-ratio:1/1}
  .cs-slide{flex-basis:86%}
  .cs-slide blockquote{font-size:23px}
  .cs-about .cs-ctas{flex-direction:column;align-items:stretch}
  .cs-about .cs-ctas .cs-btn{width:100%;font-size:15px;padding:0 16px}
}
@media (prefers-reduced-motion:reduce){.cs-hslide,.cs-hslide img{transition:none}.cs-scroll-cue::after{animation:none}.cs-mani{height:auto}.cs-mani-pin{position:static;height:auto;padding-block:80px}}
`;
